import { createFileRoute } from '@tanstack/react-router';
import * as v from 'valibot';
import { useTranslation } from '@/lib/i18n';
import { useDeferredValue, useState, useEffect, useRef, Suspense, startTransition } from 'react';
import { isServer, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { currentWindowKey, type LiveCounterMode } from '@/lib/data';
import { trpc } from '@/lib/trpc';
import { toast } from '@/lib/toast';
import { AnimatedTotalRaised } from './-components/animated-total-raised';
import { flagUrls, countryCodeToLocale } from '@/lib/country-flags';
import { Layout } from './-components/layout';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { motion, AnimatePresence } from 'motion/react';
import { APP_VERSION_STORAGE_KEY } from '@/lib/app-version';

export const Route = createFileRoute('/www/live-counter')({
	validateSearch: v.object({
		mode: v.fallback(v.picklist(['day', 'realtime']), 'day'),
	}),
	head: () => ({
		meta: [{ name: 'robots', content: 'noindex, nofollow' }],
	}),
	component: Component,
});

// ---------------------------------------------------------------------------
// Country flags (SVG icon components)
// ---------------------------------------------------------------------------

function getCountryFlagUrl(countryCode: string) {
	const locale = countryCodeToLocale[countryCode];
	return locale ? flagUrls[locale] : undefined;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ApiDonation = {
	amountCents: number;
	timestamp: number;
	campaignName: string | null;
	merchantName: string | null;
	city: string | null;
	country: string | null;
};

type FeedDonation = ApiDonation & { id: string };

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function Component() {
	const { mode } = Route.useSearch();
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();

	// Track the current 5-minute window key (same as landing page)
	const [windowKey, setWindowKey] = useState(currentWindowKey);
	useEffect(() => {
		const timer = setInterval(() => {
			const nextWindowKey = currentWindowKey();
			if (nextWindowKey !== windowKey) {
				setWindowKey(nextWindowKey);
			}
		}, 10_000);
		return () => clearInterval(timer);
	}, [mode, queryClient, windowKey]);

	// Reload the page when a new version is detected (written to localStorage by the start middleware)
	const initialVersion = useRef(isServer ? undefined : window.localStorage.getItem(APP_VERSION_STORAGE_KEY));
	useEffect(() => {
		const latestVersion = window.localStorage.getItem(APP_VERSION_STORAGE_KEY);
		if (!latestVersion || latestVersion === initialVersion.current) return;

		toast.info(`New version available: ${latestVersion.substring(0, 6)}`, {
			description: `Reloading…`,
			duration: 6_000,
		});
		const reloadTimeout = setTimeout(() => window.location.reload(), 8_000);

		return () => clearTimeout(reloadTimeout);
	}, [windowKey, mode]);

	const handleToggle = () => {
		startTransition(() => {
			void navigate({
				search: { mode: mode === 'day' ? 'realtime' : 'day' },
				replace: true,
			});
		});
	};

	return (
		<Layout className="flex min-h-dvh flex-col items-center justify-start">
			<div className="mt-[clamp(3rem,8vh,9rem)] flex w-full flex-col items-center px-6">
				<Suspense fallback={<CounterFallback />}>
					<LiveCounter windowKey={windowKey} mode={mode} />
				</Suspense>
			</div>

			<ModeToggle mode={mode} onToggle={handleToggle} />
		</Layout>
	);
}

function CounterFallback() {
	return (
		<div className="text-primary text-center text-[clamp(3.5rem,11vw,12rem)] leading-none font-bold tracking-tight">
			<span className="opacity-40">...</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Live counter with donation feed
// Same mechanism as the landing page (AnimatedTotalRaised + real timestamps)
// ---------------------------------------------------------------------------

function LiveCounter({ windowKey, mode }: { windowKey: string; mode: LiveCounterMode }) {
	const { language } = useTranslation();
	const { data } = useSuspenseQuery(
		trpc.portal.getRecentDonations.queryOptions({ windowKey, mode }, { staleTime: 10 * 60 * 1000 }),
	);
	const deferredData = useDeferredValue(data);

	// Track arrived donations for the feed (using real timestamps, same as counter)
	const arrivedIdsRef = useRef(new Set<string>());
	const [feedItems, setFeedItems] = useState<FeedDonation[]>([]);

	// Reset feed when mode changes
	const prevModeRef = useRef(mode);
	useEffect(() => {
		if (prevModeRef.current !== mode) {
			prevModeRef.current = mode;
			arrivedIdsRef.current = new Set();
			setFeedItems([]);
		}
	}, [mode]);

	// Poll for newly arrived donations at their real timestamps.
	// IDs are content-based (mode+timestamp+amount) so that:
	// 1. placeholderData from a different mode doesn't block real data (different timestamps)
	// 2. Donations keep stable IDs across refetches even if array indices shift
	// We never clear the feed — old items stay and new-window donations append
	// naturally, just like the counter transitions seamlessly.
	useEffect(() => {
		const tick = () => {
			const now = Date.now();
			let hasNew = false;
			const newArrivals: FeedDonation[] = [];
			for (const d of deferredData.donations) {
				const id = `${mode}-${d.timestamp}-${d.amountCents}`;
				if (d.timestamp <= now && !arrivedIdsRef.current.has(id)) {
					arrivedIdsRef.current.add(id);
					newArrivals.push({ ...d, id });
					hasNew = true;
				}
			}
			if (hasNew) {
				setFeedItems((prev) => [...prev, ...newArrivals].slice(-20));
			}
		};

		tick();
		const timer = setInterval(tick, 50);
		return () => clearInterval(timer);
	}, [deferredData, mode]);

	return (
		<div className="relative w-full">
			{/* Header — logo + tagline */}
			<div className="mb-6 flex flex-col items-center gap-3">
				<WorldcooLogo className="h-[clamp(3rem,6vw,8rem)] w-auto" accentClassName="text-primary" />
				<p className="text-[clamp(1rem,1.8vw,2.1rem)] tracking-wide">Donations collected</p>
			</div>

			{/* Counter — centered, stable position */}
			<div className="text-primary text-center text-[clamp(4rem,13vw,14rem)] leading-none font-bold tracking-tight">
				<AnimatedTotalRaised
					totalAtWindowStartCents={deferredData.totalAtWindowStartCents}
					donations={deferredData.donations}
					language={language}
				/>
			</div>

			{/* Feed — absolutely positioned below the counter so it never shifts it */}
			<div className="absolute inset-x-0 top-full flex justify-center px-8">
				<DonationFeed donations={feedItems} />
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** "EROSKI68 - Ayuda a víctimas - 03/2020" → "Ayuda a víctimas" */
function parseCampaignName(name: string | null): string | null {
	if (!name) return null;
	const parts = name.split(' - ');
	if (parts.length <= 1) return name.trim();
	// If the last segment looks like a date (e.g. "03/2020", "(03/2020)"), use the previous one
	const last = parts.at(-1)!.trim();
	if (/^\(?\d{1,2}\/\d{4}\)?$/.test(last) && parts.length >= 3) {
		return parts.at(-2)!.trim();
	}
	return last;
}

// ---------------------------------------------------------------------------
// Donation feed — motion layout animations
// ---------------------------------------------------------------------------

const MAX_VISIBLE = 12;

const sharedSpring = {
	type: 'spring' as const,
	stiffness: 320,
	damping: 30,
};

const enterTransition = {
	layout: sharedSpring,
	y: sharedSpring,
	opacity: { delay: 0.1, duration: 0.3, ease: 'easeOut' as const },
};

function DonationFeed({ donations }: { donations: FeedDonation[] }) {
	// Newest-first for display
	// oxlint-disable-next-line unicorn/no-array-reverse
	const allItems = [...donations].reverse();

	return (
		<div className="mt-4 w-full">
			{/* Waiting state */}
			<AnimatePresence>
				{allItems.length === 0 && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3 }}
						className="flex items-center justify-center pt-5 text-[clamp(1rem,1.3vw,1.55rem)] text-gray-300"
					>
						<span className="flex items-center gap-2">
							<span className="relative flex h-1.5 w-1.5">
								<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gray-300 opacity-75" />
								<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-gray-300" />
							</span>
							Listening for donations...
						</span>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Donation rows */}
			<div className="flex flex-col items-center">
				<AnimatePresence initial={false}>
					{allItems.map((d, i) => (
						<DonationRow key={d.id} donation={d} index={i} />
					))}
				</AnimatePresence>
			</div>
		</div>
	);
}

function DonationRow({ donation, index }: { donation: FeedDonation; index: number }) {
	const flagUrl = donation.country ? getCountryFlagUrl(donation.country) : undefined;
	const amount = (donation.amountCents / 100).toFixed(2);
	const campaignLabel = parseCampaignName(donation.campaignName);
	const hasLocation = flagUrl || donation.city;

	const targetOpacity = index >= MAX_VISIBLE ? 0 : index === 0 ? 1 : Math.max(0.25, 1 - index * 0.1);

	return (
		<motion.div
			layout
			initial={{ opacity: 0, y: -30 }}
			animate={{ opacity: targetOpacity, y: 0 }}
			transition={enterTransition}
			className="w-full"
		>
			<div className="flex flex-col items-center justify-center px-2 py-2">
				<div className="flex flex-wrap items-center justify-center gap-2">
					<span className="text-primary shrink-0 text-[clamp(1.75rem,2.9vw,3.5rem)] font-semibold tabular-nums">
						+{amount}€
					</span>
					{campaignLabel && (
						<>
							<span className="shrink-0 text-[clamp(1.35rem,2.1vw,2.5rem)] text-gray-400">para</span>
							<span className="text-[clamp(1.35rem,2.1vw,2.5rem)] font-medium text-gray-700">{campaignLabel}</span>
						</>
					)}
				</div>

				<div className="flex flex-wrap items-center justify-center gap-2">
					{donation.merchantName && (
						<span className="text-[clamp(1.35rem,2.1vw,2.5rem)] text-gray-400">
							en {donation.merchantName}
							{hasLocation && ', '}
						</span>
					)}
					{hasLocation && (
						<span className="flex items-center gap-1.5 text-[clamp(1.35rem,2.1vw,2.5rem)] text-gray-400">
							{donation.city}
							{flagUrl && (
								<img src={flagUrl} alt="" className="h-[clamp(1.1rem,1.7vw,2rem)] w-[clamp(1.1rem,1.7vw,2rem)]" />
							)}
						</span>
					)}
				</div>
			</div>
		</motion.div>
	);
}

// ---------------------------------------------------------------------------
// Mode toggle — switch between day offset and realtime
// ---------------------------------------------------------------------------

function ModeToggle({ mode, onToggle }: { mode: LiveCounterMode; onToggle: () => void }) {
	const isRealtime = mode === 'realtime';

	return (
		<div className="fixed right-4 bottom-4 flex items-center gap-2.5 rounded-full bg-white/80 px-3 py-2 shadow-md backdrop-blur-sm">
			<span className={`text-xs font-medium ${!isRealtime ? 'text-gray-900' : 'text-gray-400'}`}>-1 day</span>
			<button
				type="button"
				role="switch"
				aria-checked={isRealtime}
				aria-label={`Switch to ${isRealtime ? 'day offset' : 'realtime'} mode`}
				onClick={onToggle}
				className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
					isRealtime ? 'bg-primary' : 'bg-gray-300'
				}`}
			>
				<span
					className={`pointer-events-none inline-block h-4 w-4 translate-y-0.5 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${
						isRealtime ? 'translate-x-[18px]' : 'translate-x-0.5'
					}`}
				/>
			</button>
			<span className={`text-xs font-medium ${isRealtime ? 'text-gray-900' : 'text-gray-400'}`}>Realtime</span>
		</div>
	);
}
